import {
  QueryClient,
  UseMutationResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosProgressEvent } from "axios";
import { chunk, isEqual } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  LOB,
  UpdateAddresses,
  UpdateReport,
  addComment,
  addManualAddress,
  deleteComment,
  editComment,
  calculatePremium,
  createCRMContact,
  deleteAdditionalFile,
  deleteAttachment,
  deleteEmailThread,
  deleteReport,
  fetchEmailThreads,
  getBatchComments,
  getClearanceEmailRecipients,
  generatePolicyNumbers,
  LocationClassification,
  LocationClassificationKey,
  LocationKey,
  getDeductibleSummary,
  getLocationClassifications,
  getGoogleGroupStats,
  getIntegrationData,
  getLimitSummary,
  getUpdatingReports,
  listAdditionalFiles,
  listCRMContacts,
  listPolicyFlyApplications,
  listReport,
  listReports,
  listUsers,
  loadAddressesForMap,
  ratePrecheck,
  loadAddresses,
  updateAddresses,
  updateCRMContact,
  updateReport,
  uploadAdditionalFile,
  uploadEmailThread,
} from "../../api/routes";
import * as client from "../../client";
import { useClientId } from "../../ClientConfig";
import { useBoundedQueries } from "../../util";
import {
  applyOverridesToMetadata,
  applyOverridesToMetadataReponse,
  SafeReportMetadata,
} from "./Conversion";
import {
  addressesForMapKeys,
  commentKeys,
  crmContactKeys,
  googleGroupStatsKeys,
  jobKeys,
  policyFlyKeys,
  reportKeys,
  updatingReportKeys,
  userKeys,
  windTierKeys,
} from "./queryKeys";

// Invalidate everything cached under a report's detail prefix (the report
// itself, premiums, prechecks, summaries, files, threads, ...) except the
// exact keys the caller just wrote via setQueryData — invalidating a patched
// key would kick off a refetch that races with, and then overwrites, the
// patch.
const invalidateReportDetail = (
  queryClient: QueryClient,
  reportId: string,
  exceptPatched: ReadonlyArray<readonly unknown[]>
): void => {
  queryClient.invalidateQueries({
    queryKey: reportKeys.detail(reportId),
    predicate: (query) =>
      !exceptPatched.some((key) => isEqual(query.queryKey, key)),
  });
};

const useReportsInner = (lobs: LOB[]) =>
  useQuery({
    enabled: lobs.length > 0,
    queryKey: reportKeys.list(lobs),
    queryFn: () => listReports(lobs),
    // Refetch every 2 minutes to avoid really stale data showing on the dashboard
    refetchInterval: 120_000,
  });

export const useReports = (lobs: LOB[]) => useReportsInner(lobs);
export const useSafeReports = (lobs: LOB[]) => {
  const clientId = useClientId();
  const reports = useReportsInner(lobs);
  return useMemo(
    () => ({
      ...reports,
      data: reports.data
        ? reports.data.map((report) =>
            applyOverridesToMetadataReponse(report, clientId)
          )
        : reports.data,
    }),
    [reports.data]
  );
};

export const usePolicyFlyApplications = (statuses: string) => {
  const clientId = useClientId();
  return useQuery({
    queryKey: policyFlyKeys.list(statuses),
    queryFn: async (): Promise<SafePolicyFlyApplicationRow[]> => {
      const raw = await listPolicyFlyApplications(statuses);
      return raw.map((app) => ({
        ...app,
        report_metadata: applyOverridesToMetadata(
          app.report_metadata,
          clientId
        ),
      }));
    },
    refetchInterval: 120_000,
  });
};
export type SafePolicyFlyApplicationRow = Omit<
  client.PolicyFlyApplicationRow,
  "report_metadata"
> & { report_metadata: SafeReportMetadata };

export const useReport = (reportId?: string) => {
  return useQuery({
    refetchOnWindowFocus: false,
    queryKey: reportKeys.detail(reportId ?? ""),
    queryFn: async (): Promise<client.ReportResponse | undefined> => {
      if (!reportId) return undefined;

      const report = await listReport(reportId, false);

      const addressesBySov = await Promise.all(
        report.report_json.sovs.map(async (sov) => {
          const { addresses } = await loadAddresses(reportId, sov.sov_id);
          const live = addresses.filter((a) => !a.is_deleted);
          live.sort((a, b) =>
            (a.default_sort_key ?? "").localeCompare(b.default_sort_key ?? "")
          );
          return [sov.sov_id, live] as const;
        })
      );

      const bySovId = Object.fromEntries(addressesBySov);
      return {
        ...report,
        report_json: {
          ...report.report_json,
          sovs: report.report_json.sovs.map((sov) => ({
            ...sov,
            addresses: bySovId[sov.sov_id] ?? [],
          })),
        },
      };
    },
  });
};

export const useUpdateAddresses = (reportId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["update-addresses", reportId],
    mutationFn: async (update: UpdateAddresses) => {
      return await updateAddresses(reportId, update);
    },
    onSuccess: (data) => {
      // Invalidate report list data because updating addresses can affect TIV/other metadata shown in the list field.
      queryClient.invalidateQueries({ queryKey: reportKeys.lists() });
      // Premiums and summaries are derived from address data; refresh them
      // but keep the detail query, which we patch in place below.
      invalidateReportDetail(queryClient, reportId, [
        reportKeys.detail(reportId),
      ]);

      const prev = queryClient.getQueryData<client.ReportResponse>(
        reportKeys.detail(reportId)
      );

      const updates = Object.fromEntries(
        data.addresses.map((a) => [a.address_id, a])
      );
      if (prev) {
        queryClient.setQueryData<client.ReportResponse>(
          reportKeys.detail(reportId),
          {
            ...prev,
            report_json: {
              ...prev.report_json,
              sovs: prev.report_json.sovs.map((sov) => ({
                ...sov,
                addresses: sov.addresses.map((a) => updates[a.address_id] ?? a),
              })),
            },
          }
        );
      }
    },
  });
};

export const useAddManualAddress = (reportId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["add-manual-address", reportId],
    mutationFn: async (address: client.RawAddress) => {
      return await addManualAddress(reportId, address);
    },
    onSuccess: (data) => {
      // Invalidate report list data because adding addresses can affect TIV/other metadata shown in the list field.
      queryClient.invalidateQueries({ queryKey: reportKeys.lists() });
      // Premiums and summaries are derived from address data; refresh them
      // but keep the detail query, which we replace wholesale below.
      invalidateReportDetail(queryClient, reportId, [
        reportKeys.detail(reportId),
      ]);

      queryClient.setQueryData(reportKeys.detail(reportId), data.report);
    },
  });
};

export const useDeleteReport = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["delete-report"],
    mutationFn: async (reportId: string) => {
      return await deleteReport(reportId);
    },
    onSuccess: (_data, reportId) => {
      // Drop the deleted report's cached detail (and everything derived from
      // it) and refresh the dashboard list so it no longer shows the removed
      // submission.
      queryClient.removeQueries({ queryKey: reportKeys.detail(reportId) });
      queryClient.invalidateQueries({ queryKey: reportKeys.lists() });
    },
  });
};

export const useCalculatedPremium = (reportId: string, enabled: boolean) => {
  return useQuery({
    enabled,
    queryKey: reportKeys.calculatedPremium(reportId),
    queryFn: () => calculatePremium(reportId),
    refetchOnWindowFocus: false,
  });
};

export const useRatePrecheck = (reportId: string) => {
  return useQuery({
    queryKey: reportKeys.ratePrecheck(reportId),
    queryFn: () => ratePrecheck(reportId),
  });
};

// Return type is intentionally "unknown" here so that callers don't use it
// directly, since it requires some interpretation. Instead, use useReport to
// read report data.
export const useUpdateReport = (
  reportId: string
): UseMutationResult<unknown, unknown, UpdateReport, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["update-report", reportId],
    mutationFn: async (update: UpdateReport) => {
      return await updateReport(reportId, update);
    },
    onSuccess: (updateResult) => {
      const thisReportKey = reportKeys.detail(reportId);
      // Because the dashboard computes things like enabled TIV from the report,
      // conservatively assume that any change to the report could affect the
      // list view.
      queryClient.invalidateQueries({ queryKey: reportKeys.lists() });
      // Refresh everything derived from this report, but not the detail query
      // itself — we reconcile it from the mutation response below.
      invalidateReportDetail(queryClient, reportId, [thisReportKey]);
      // Trigger a re-fetch of updating reports immediately
      queryClient.invalidateQueries({ queryKey: updatingReportKeys.all });

      const oldData =
        queryClient.getQueryData<client.ReportResponse>(thisReportKey);

      if (updateResult.use_old_sovs && oldData) {
        // Backend says to use old SOV data and we have data
        const newData = updateResult.report;
        queryClient.setQueryData<client.ReportResponse>(thisReportKey, {
          ...newData,
          report_json: {
            ...newData.report_json,
            sovs: oldData.report_json.sovs,
          },
        });
      } else if (!updateResult.use_old_sovs) {
        // Backend says to use new SOV data
        queryClient.setQueryData(thisReportKey, updateResult.report);
      } else {
        // Backend says to use old data but we don't have any. Shouldn't happen,
        // but in this case we fall back to a full reload.
        queryClient.invalidateQueries({ queryKey: thisReportKey });
      }
    },
  });
};

// Keyed under the report's detail prefix so the invalidation in
// useUpdateReport.onSuccess refetches the recipients automatically after an
// edit.
export const useClearanceRecipients = (reportId: string, enabled: boolean) => {
  return useQuery({
    enabled,
    queryKey: reportKeys.clearanceRecipients(reportId),
    queryFn: () => getClearanceEmailRecipients(reportId),
  });
};

export const useUsers = () => {
  return useQuery({
    queryKey: userKeys.all,
    queryFn: () => listUsers(),
    // Re-use the result for at least 5 seconds (just avoid re-fetching during load of the page, etc.)
    staleTime: 5000,
  });
};

export const useLocalUser = () => {
  return useUsers().data?.current_user;
};

export const useUserById = (
  userId: string | undefined | null
): client.User | undefined => {
  const users = useUsers();
  if (!userId) return undefined;
  return (users?.data?.all_company_users ?? []).find((u) => u.id === userId);
};

export const useCRMContacts = () => {
  return useQuery({
    queryKey: crmContactKeys.all,
    queryFn: () => listCRMContacts(),
    // Keep data fresh for 5 minutes before considering a refetch
    staleTime: 5 * 60 * 1000,
    // Keep data in cache for 10 minutes even when unused
    gcTime: 10 * 60 * 1000,
    // Don't refetch when window regains focus
    refetchOnWindowFocus: false,
  });
};

/**
 * Merge a single contact into the cached `crm-contacts` list.
 * - Replaces an existing contact with the same id.
 * - Prepends the contact when it wasn't present (default behavior).
 * - Returns the new `ListContactsResponse` shape.
 */
function mergeContactIntoCache(
  queryClient: QueryClient,
  contact: client.ReportContact
): void {
  queryClient.setQueryData(
    crmContactKeys.all,
    (prev: client.ListContactsResponse | undefined) => {
      const existing = prev?.contacts ?? [];
      const replaced = existing.map((c) => (c.id === contact.id ? contact : c));
      const has = replaced.some((c) => c.id === contact.id);

      const contacts: client.ReportContact[] = has
        ? replaced
        : [contact, ...replaced];

      return { ...(prev ?? {}), contacts };
    }
  );
}
export const useCreateCRMContact = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["crm-create-contact"],
    mutationFn: async (body: client.CreateContactRequest) => {
      return await createCRMContact(body);
    },
    onSuccess: (response) => {
      if (response && response.contact) {
        mergeContactIntoCache(queryClient, response.contact);
      } else {
        queryClient.invalidateQueries({ queryKey: crmContactKeys.all });
      }
    },
  });
};

export const useUpdateCRMContact = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["crm-update-contact"],
    mutationFn: async ({
      id,
      body,
    }: {
      id: string;
      body: client.UpdateContactRequest;
    }) => {
      return await updateCRMContact(id, body);
    },
    onSuccess: (response) => {
      if (response && response.contact) {
        mergeContactIntoCache(queryClient, response.contact);
      } else {
        queryClient.invalidateQueries({ queryKey: crmContactKeys.all });
      }
      // Reports embed contact data in both list rows and details, so refresh
      // everything report-related.
      queryClient.invalidateQueries({ queryKey: reportKeys.all });
    },
  });
};

export const useAdditionalFiles = (
  reportId: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: reportKeys.additionalFiles(reportId),
    queryFn: () => listAdditionalFiles(reportId),
    enabled,
  });
};

export const useUploadAdditionalFiles = (
  reportId: string,
  onProgress: (e: AxiosProgressEvent) => void,
  isScreenshot: boolean
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["upload-additional-file", reportId],
    mutationFn: (files: File[]) =>
      uploadAdditionalFile(reportId, files, onProgress, isScreenshot),
    onSuccess: (response) => {
      // Refresh the report and its derived data; the files list itself is
      // patched from the response below.
      invalidateReportDetail(queryClient, reportId, [
        reportKeys.additionalFiles(reportId),
      ]);
      queryClient.setQueryData(reportKeys.additionalFiles(reportId), response);
    },
  });
};

export const useDeleteAdditionalFile = (reportId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["delete-additional-file", reportId],
    mutationFn: (names: string[]) => deleteAdditionalFile(reportId, names),
    onSuccess: (response) => {
      queryClient.setQueryData(reportKeys.additionalFiles(reportId), response);
    },
  });
};

export const useDeleteAttachment = (reportId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["delete-attachment", reportId],
    mutationFn: (filename: string) => deleteAttachment(reportId, filename),
    onSuccess: (attachmentInfo, filename) => {
      // The delete just removes the attachment_info entry; patch it into the
      // cached report so the attachment disappears without a full refetch.
      const prev = queryClient.getQueryData<client.ReportResponse>(
        reportKeys.detail(reportId)
      );
      if (prev) {
        queryClient.setQueryData<client.ReportResponse>(
          reportKeys.detail(reportId),
          {
            ...prev,
            report_json: {
              ...prev.report_json,
              attachment_info: attachmentInfo,
            },
          }
        );
      }
      // The blob was moved out of additional_files/, so drop it from that cache
      // too; otherwise it would resurface as an unclassified file.
      queryClient.setQueryData<string[]>(
        reportKeys.additionalFiles(reportId),
        (files) => (files ?? []).filter((f) => f !== filename)
      );
      // Deleting an attachment reprocesses the components it impacted, so
      // surface the rerunning state, refresh the dashboard list, and refresh
      // this report's derived data — skipping the two entries just patched
      // above.
      queryClient.invalidateQueries({ queryKey: reportKeys.lists() });
      invalidateReportDetail(queryClient, reportId, [
        reportKeys.detail(reportId),
        reportKeys.additionalFiles(reportId),
      ]);
      queryClient.invalidateQueries({ queryKey: updatingReportKeys.all });
    },
  });
};

export const useAddressesForMap = (lobs: LOB[]) => {
  return useQuery({
    queryKey: addressesForMapKeys.list(lobs),
    queryFn: () => loadAddressesForMap(lobs),
    refetchOnWindowFocus: false,
    staleTime: 3_600_000,
  });
};

export const useComments = (reportIds: string[]) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: commentKeys.batch(reportIds),
    queryFn: async () => {
      const data = await getBatchComments(reportIds);
      for (const [reportId, comments] of Object.entries(
        data.comments_by_report_id
      )) {
        queryClient.setQueryData(commentKeys.forReport(reportId), comments);
      }
      return data;
    },
    enabled: reportIds.length > 0,
    refetchOnWindowFocus: false,
  });
};

export const useCommentsForReport = (
  reportId: string,
  fetchEnabled: boolean = true
) => {
  return useQuery<client.Comments>({
    queryKey: commentKeys.forReport(reportId),
    queryFn: async () => {
      const data = await getBatchComments([reportId]);
      return data.comments_by_report_id[reportId] ?? { all_comments: [] };
    },
    enabled: fetchEnabled,
    refetchOnWindowFocus: false,
  });
};

export const useAddComment = (reportId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["add-comment", reportId],
    mutationFn: async (message: string) => {
      return await addComment(reportId, message);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(commentKeys.forReport(reportId), data.comments);
    },
  });
};

export const useDeleteComment = (reportId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["delete-comment", reportId],
    mutationFn: async (commentId: string) => {
      return await deleteComment(reportId, commentId);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(commentKeys.forReport(reportId), data.comments);
    },
  });
};

export const useEditComment = (reportId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["edit-comment", reportId],
    mutationFn: async ({
      commentId,
      message,
    }: {
      commentId: string;
      message: string;
    }) => {
      return await editComment(reportId, commentId, message);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(commentKeys.forReport(reportId), data.comments);
    },
  });
};

export const useUpdatingReports = () => {
  const queryClient = useQueryClient();
  const doneUpdates = useRef<Set<string>>(new Set());
  return useQuery({
    queryKey: updatingReportKeys.all,
    queryFn: async (): Promise<client.ListUpdatingReportsResponse> => {
      const response = await getUpdatingReports(
        new Date(performance.timeOrigin)
      );

      // If any report finishes, invalidate everything cached for it — the
      // detail prefix covers the report itself plus all derived data
      // (premiums, summaries, integration data, files, threads).
      for (const update of response.updates) {
        if (update.outcome && !doneUpdates.current.has(update.update_id)) {
          doneUpdates.current.add(update.update_id);
          queryClient.invalidateQueries({
            queryKey: reportKeys.detail(update.report_id),
          });
        }
      }

      return response;
    },
    refetchInterval: 5_000,
    staleTime: 5_000,
  });
};

const INTEGRATION_TYPES_THAT_BLOCK_EDITS = ["RERUN_REPORT", "POLICY_FLY"];
export const useThisReportIsUpdating = (
  reportId: string,
  integrationType?: string
): { updating: client.ReportBeingUpdated } | undefined => {
  const updating = useUpdatingReports().data?.updates.find(
    (u) =>
      u.report_id === reportId &&
      !u.outcome &&
      INTEGRATION_TYPES_THAT_BLOCK_EDITS.includes(u.integration_type) &&
      (!integrationType || u.integration_type === integrationType)
  );
  return useMemo(() => updating && { updating }, [updating]);
};

export const useAllUpdatesForReport = (
  reportId: string,
  integrationType?: string
): client.ReportBeingUpdated[] => {
  const updates = useUpdatingReports().data?.updates ?? [];
  return useMemo(
    () =>
      updates.filter(
        (u) =>
          u.report_id === reportId &&
          !u.outcome &&
          (!integrationType || u.integration_type === integrationType)
      ),
    [updates, reportId, integrationType]
  );
};

export const useCancelJobs = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["cancel-jobs"],
    mutationFn: async (updateIds: string[]) => {
      await Promise.all(
        updateIds.map((id) =>
          client.asgardCancelJobAsgardCancelJobPost({
            requestBody: { report_integration_id: id },
          })
        )
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: updatingReportKeys.all });
      queryClient.invalidateQueries({ queryKey: jobKeys.allQueueJobs });
    },
  });
};

export const useEmailThreads = (reportId: string, enabled: boolean = true) => {
  return useQuery({
    enabled: enabled,
    queryKey: reportKeys.emailThreads(reportId),
    queryFn: () => fetchEmailThreads(reportId),
    refetchOnWindowFocus: false,
  });
};

export const useUploadEmailThread = (reportId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["upload-email-thread", reportId],
    mutationFn: (files: File[]) => uploadEmailThread(reportId, files),
    onSuccess: () => {
      // The detail prefix covers the report, its email threads, and all
      // other derived data.
      queryClient.invalidateQueries({ queryKey: reportKeys.detail(reportId) });
    },
  });
};

export const useDeleteEmailThread = (reportId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["delete-email-thread", reportId],
    mutationFn: (conversationId: string) =>
      deleteEmailThread(reportId, conversationId),
    onSuccess: () => {
      // The detail prefix covers the report, its email threads, and its
      // additional-files list.
      queryClient.invalidateQueries({ queryKey: reportKeys.detail(reportId) });
    },
  });
};

export const useGeneratePolicyNumbers = (reportId: string) => {
  return useMutation({
    mutationKey: ["generate-policy-numbers", reportId],
    mutationFn: async () => {
      return await generatePolicyNumbers(reportId);
    },
  });
};

// Per-hook bound on concurrent in-flight chunks. With three hooks
// (deductible, limit, integration-data) the global cap is ~3×this.
const MAX_CONCURRENT_CHUNKS = 2;

type AddressSummaryQueryResult<T> = {
  // `addresses` is a mutable map kept in a ref across renders — its
  // identity is stable, contents grow as chunks settle. Consumers depend
  // on `version` for change detection, not on the map identity.
  data: { addresses: { [addressId: string]: T }; version: number } | undefined;
  isLoading: boolean;
  hasError: boolean;
  loadedChunks: number;
  totalChunks: number;
};

const useChunkedAddressSummary = <T>(
  queryKeyPrefix: readonly unknown[],
  addressIds: string[],
  fetcher: (chunk: string[]) => Promise<{ addresses: { [k: string]: T } }>,
  chunkSize: number
): AddressSummaryQueryResult<T> => {
  const chunks = useMemo(() => chunk(addressIds, chunkSize), [addressIds]);

  const queries = useBoundedQueries<{ addresses: { [k: string]: T } }>(
    chunks.map((chunk) => ({
      queryKey: [...queryKeyPrefix, chunk[0]],
      queryFn: () => fetcher(chunk),
      refetchOnWindowFocus: false,
    })),
    MAX_CONCURRENT_CHUNKS,
    chunks
  );

  // Merged map is held in a ref and mutated in place across renders.
  // Total work to absorb N items across all chunks is O(N) — versus the
  // O(N²/chunk_size) cost of building a fresh merged object on every chunk
  // arrival. `seenChunkDataRef` tracks which result objects have already
  // been merged so the absorb effect is idempotent.
  const mergedRef = useRef<{ [k: string]: T }>({});
  const seenChunkDataRef = useRef<Set<object>>(new Set());
  const [version, setVersion] = useState(0);

  // Reset accumulator when the chunk list itself changes (different
  // addressIds → different report or enabled-SOV set). Sliding-window
  // reset is owned by useBoundedQueries.
  useEffect(() => {
    mergedRef.current = {};
    seenChunkDataRef.current = new Set();
    setVersion((v) => v + 1);
  }, [chunks]);

  // Absorb any chunks that have newly settled. Idempotent — chunks already
  // merged (tracked by their data reference) are skipped. Depends on a
  // content signature rather than `queries` itself, which would re-fire on
  // every parent render.
  const dataSignature = queries.map((q) => q.dataUpdatedAt ?? 0).join(",");
  useEffect(() => {
    let added = false;
    for (const q of queries) {
      if (q.data !== undefined && !seenChunkDataRef.current.has(q.data)) {
        Object.assign(mergedRef.current, q.data.addresses);
        seenChunkDataRef.current.add(q.data);
        added = true;
      }
    }
    if (added) setVersion((v) => v + 1);
    // queries excluded — dataSignature captures the content-relevant subset;
    // including it would invalidate on every render.
  }, [dataSignature]); // eslint-disable-line

  const settledCount = queries.filter(
    (q) => q.data !== undefined || q.isError
  ).length;
  const succeededCount = queries.filter((q) => q.data !== undefined).length;
  const hasError = queries.some((q) => q.isError);

  return useMemo(
    () => ({
      data:
        chunks.length === 0 || succeededCount > 0
          ? { addresses: mergedRef.current, version }
          : undefined,
      isLoading: chunks.length > 0 && settledCount < chunks.length,
      hasError,
      loadedChunks: succeededCount,
      totalChunks: chunks.length,
    }),
    [version, succeededCount, settledCount, hasError, chunks.length]
  );
};

export const useDeductibleSummary = (reportId: string, addressIds: string[]) =>
  useChunkedAddressSummary<{ [perilCode: string]: client.DeductibleSummary }>(
    reportKeys.deductibleSummary(reportId),
    addressIds,
    (chunk) => getDeductibleSummary(reportId, chunk),
    client.$DeductibleSummaryRequest.properties.address_ids.maxItems
  );

export const useLimitSummary = (reportId: string, addressIds: string[]) =>
  useChunkedAddressSummary<{ [perilCode: string]: client.LimitSummary }>(
    reportKeys.limitSummary(reportId),
    addressIds,
    (chunk) => getLimitSummary(reportId, chunk),
    client.$LimitSummaryRequest.properties.address_ids.maxItems
  );

export const useIntegrationData = (
  reportId: string,
  types: client.IntegrationDataRequest["types"],
  addressIds: string[]
) =>
  useChunkedAddressSummary<client.AddressIntegrationData>(
    reportKeys.integrationData(reportId, types),
    addressIds,
    (chunk) => getIntegrationData(reportId, types, chunk),
    client.$IntegrationDataRequest.properties.address_ids.maxItems
  );

export const useGoogleGroupStats = (email: string | null) =>
  useQuery({
    enabled: !!email,
    queryKey: googleGroupStatsKeys.forEmail(email ?? ""),
    queryFn: () => getGoogleGroupStats(email!),
    refetchOnWindowFocus: false,
  });

export const locationClassificationKey = (
  state: string | null | undefined,
  county: string | null | undefined
): LocationClassificationKey =>
  `${state ?? ""}|${county ?? ""}` as LocationClassificationKey;

// Catastrophe gate classification (wind tier, wind gate, EQ gate) for the SOV
// pivot panel. Takes addresses, derives the unique (state, county) pairs, and
// requests classifications for just those — request size scales with unique
// counties, not address count.
export const useLocationClassifications = (
  addresses: {
    state: string | null | undefined;
    county: string | null | undefined;
  }[]
) => {
  // Dedupe into a list of unique LocationKey payloads, sorted so the
  // react-query cacheKey is stable across re-orderings of the input.
  const locations: LocationKey[] = useMemo(() => {
    const seen = new Set<LocationClassificationKey>();
    const out: LocationKey[] = [];
    for (const a of addresses) {
      const key = locationClassificationKey(a.state, a.county);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ state: a.state ?? null, county: a.county ?? null });
    }
    out.sort((a, b) =>
      locationClassificationKey(a.state, a.county).localeCompare(
        locationClassificationKey(b.state, b.county)
      )
    );
    return out;
  }, [addresses]);

  const cacheKey = useMemo(
    () =>
      locations
        .map((l) => locationClassificationKey(l.state, l.county))
        .join(","),
    [locations]
  );

  return useQuery({
    enabled: locations.length > 0,
    queryKey: windTierKeys.forLocations(cacheKey),
    queryFn: async () => {
      const response = await getLocationClassifications(locations);
      const map = new Map<LocationClassificationKey, LocationClassification>();
      for (const r of response.results) {
        map.set(locationClassificationKey(r.state, r.county), {
          windTier: r.wind_tier,
          windGate: r.wind_gate,
          eqGate: r.eq_gate,
        });
      }
      return map;
    },
    refetchOnWindowFocus: false,
  });
};
